import { useState } from 'react';

import {
  AppIcon,
  CareerPathCompensation,
  CareerPathSampleJobs,
  CAREER_TRACK_COLORS,
  CareerTrackRadio,
  SourcesModal,
  useContainerQuery,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Theme,
  SxProps,
  Typography,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';

type CompensationTableProps = Readonly<{
  data: Record<string, CareerPathCompensation[]>;
  sampleJobs: CareerPathSampleJobs;
  careerPathName: string;
}>;

const formatSalaryRange = (min?: number | null, max?: number | null): string => {
  if (min == null) return 'N/A';
  if (max == null) return `$${min / 1000}k+`;

  return `$${min / 1000}k - $${max / 1000}k`;
};

const STRINGS = appStrings.modules.careerPath.detailPage.earningPotential.compensationTable;

const tableRows = Object.values(STRINGS.rows);

function DataSourcesButton({ careerPathName }: { careerPathName: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Box
        id="compensation-table-data-sources-btn"
        onClick={() => setIsOpen(true)}
        sx={dataSourcesIconSx}
      >
        <AppIcon iconName="InfoCircle" diameter="16px" color="Colors/Foreground/fg-quinary" />
      </Box>
      <SourcesModal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        description={STRINGS.dataSources.description}
        sources={STRINGS.dataSources.sources}
        feedback={{
          url: STRINGS.dataSources.feedback.url,
          label: STRINGS.dataSources.feedback.label,
          subject: STRINGS.dataSources.feedback.subject(careerPathName),
        }}
      />
    </>
  );
}

export function CompensationTable({ data, sampleJobs, careerPathName }: CompensationTableProps) {
  const [containerRef, isCompact] = useContainerQuery(600);
  const trackNames = Object.keys(data);
  const [selectedTrackIndex, setSelectedTrackIndex] = useState(0);

  const handleTrackChange = (event: SelectChangeEvent<number>) => {
    setSelectedTrackIndex(event.target.value as number);
  };

  if (isCompact) {
    const selectedTrack = trackNames[selectedTrackIndex];
    const comp = selectedTrack ? data[selectedTrack]?.[0] : undefined;
    const jobs = selectedTrack ? sampleJobs.tiers[selectedTrack] || [] : [];

    return (
      <Box ref={containerRef} sx={mobileContainerSx}>
        <DataSourcesButton careerPathName={careerPathName} />
        {/* Header with track selector */}
        <Box sx={mobileHeaderSx}>
          <Select
            id="compensation-track-select"
            value={selectedTrackIndex}
            onChange={handleTrackChange}
            sx={trackSelectSx}
            IconComponent={() => (
              <Box sx={selectIconSx}>
                <AppIcon
                  iconName="ChevronDown"
                  diameter="20px"
                  color="Colors/Foreground/fg-quaternary"
                />
              </Box>
            )}
            renderValue={(value) => {
              const trackName = trackNames[value as number];

              return (
                <Box sx={trackSelectLabelSx}>
                  <CareerTrackRadio careerTrack={trackName || ''} />
                  <Typography variant="text-sm-medium">{trackName}</Typography>
                </Box>
              );
            }}
          >
            {trackNames.map((trackName, index) => (
              <MenuItem key={trackName} value={index}>
                <Box sx={trackSelectLabelSx}>
                  <CareerTrackRadio careerTrack={trackName} />
                  <Typography variant="text-sm-medium">{trackName}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Salary rows */}
        {comp && (
          <Box sx={mobileDataContainerSx}>
            <MobileDataRow
              label={STRINGS.rows.entryLevel}
              value={formatSalaryRange(comp.entry_level_min, comp.entry_level_max)}
            />
            <MobileDataRow
              label={STRINGS.rows.midCareer}
              value={formatSalaryRange(comp.mid_career_min, comp.mid_career_max)}
            />
            <MobileDataRow
              label={STRINGS.rows.lateCareer}
              value={formatSalaryRange(comp.late_career_min, comp.late_career_max)}
            />

            {/* Sample jobs */}
            <Box sx={mobileRowSx}>
              <Typography variant="text-xs-semibold" sx={mobileLabelSx}>
                {STRINGS.rows.sampleJobRoles}
              </Typography>
              <Box sx={jobRolesContainerStyles}>
                {jobs.length > 0 ? (
                  jobs.map((role) => (
                    <Chip
                      key={role.id}
                      label={role.name}
                      size="sm"
                      variant="badge"
                      sx={jobRoleChipStyles}
                    />
                  ))
                ) : (
                  <Typography variant="text-sm-regular" color="text.secondary">
                    {STRINGS.NA}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box ref={containerRef} sx={containerStyles}>
      <DataSourcesButton careerPathName={careerPathName} />
      <Table sx={tableStyles}>
        <colgroup>
          <col style={{ width: '18%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '15%' }} />
          <col />
        </colgroup>
        <TableHead>
          <TableRow sx={tableHeaderRowStyles}>
            {tableRows.map((tr, index) => (
              <TableCell key={tr} sx={index === 0 ? firstHeaderCellStyles : tableHeaderCellStyles}>
                {tr}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(data).map(([trackName, compensations]) => {
            const comp = compensations[0];

            if (!comp) return null;

            const trackColor = CAREER_TRACK_COLORS[trackName];

            if (!trackColor) return null;

            const jobs = sampleJobs.tiers[trackName] || [];

            return (
              <TableRow key={trackName} sx={tableRowStyles}>
                <TableCell>
                  <Box sx={trackCellStyles}>
                    <CareerTrackRadio careerTrack={trackName} />
                    <Typography variant="text-sm-medium" sx={trackNameTextSx}>
                      {trackName.replace(/\//g, '/\u200B')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={salaryTextSx}>
                  {formatSalaryRange(comp.entry_level_min, comp.entry_level_max)}
                </TableCell>
                <TableCell sx={salaryTextSx}>
                  {formatSalaryRange(comp.mid_career_min, comp.mid_career_max)}
                </TableCell>
                <TableCell sx={salaryTextSx}>
                  {formatSalaryRange(comp.late_career_min, comp.late_career_max)}
                </TableCell>
                <TableCell>
                  <Box sx={jobRolesContainerStyles}>
                    {jobs.length > 0 ? (
                      jobs.map((role) => (
                        <Chip
                          key={role.id}
                          label={role.name}
                          size="sm"
                          variant="badge"
                          sx={jobRoleChipStyles}
                        />
                      ))
                    ) : (
                      <Typography variant="text-sm-regular" color="text.secondary">
                        {STRINGS.NA}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

function MobileDataRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={mobileRowSx}>
      <Typography variant="text-xs-semibold" sx={mobileLabelSx}>
        {label}
      </Typography>
      <Typography variant="text-sm-regular" sx={mobileValueSx}>
        {value}
      </Typography>
    </Box>
  );
}

// ── Desktop styles ──

const containerStyles: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  borderRadius: backrs.radius['radius-lg'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary-hover']}`,
  overflow: 'hidden',
  containerType: 'inline-size',
});

const tableStyles: SxProps<Theme> = ({ backrs }) => ({
  borderRadius: backrs.radius['radius-xl'],
  overflow: 'hidden',
  background: backrs.colors['Colors/Background/bg-primary'],
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    paddingTop: backrs.spacing['spacing-md'],
    paddingBottom: backrs.spacing['spacing-md'],
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    height: '72px',
  },
});

const tableHeaderRowStyles: SxProps<Theme> = ({ backrs }) => ({
  background: backrs.colors['Colors/Background/bg-secondary'],
  columnGap: backrs.spacing['spacing-3xl'],
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  '& .MuiTableCell-root': {
    paddingTop: backrs.spacing['spacing-xl'],
    paddingBottom: backrs.spacing['spacing-xl'],
  },
});

const tableHeaderCellStyles: SxProps<Theme> = ({ typography, backrs }) => ({
  ...typography['text-xs-semibold'],
  color: backrs.colors['Colors/Text/text-quaternary'],
  padding: `${backrs.spacing['spacing-lg']} ${backrs.spacing['spacing-md']}`,
});

const firstHeaderCellStyles: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-xs-semibold'],
  color: backrs.colors['Colors/Text/text-quaternary'],
  padding: `${backrs.spacing['spacing-lg']} ${backrs.spacing['spacing-md']}`,
  pl: `calc(${backrs.spacing['spacing-6xl']} + ${backrs.spacing['spacing-sm']})`,
  width: '18%',
});

const tableRowStyles: SxProps<Theme> = ({ backrs }) => ({
  background: backrs.colors['Colors/Background/bg-primary'],
  '&:last-child': {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
});

const trackNameTextSx: SxProps<Theme> = {
  whiteSpace: 'nowrap',
  '@container (max-width: 800px)': {
    whiteSpace: 'normal',
  },
};

const trackCellStyles: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});

const jobRolesContainerStyles: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-sm'],
});

const jobRoleChipStyles: SxProps<Theme> = ({ backrs }) => ({
  boxShadow: backrs.shadows['shadow-xs'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  color: backrs.colors['Colors/Text/text-secondary'],
  padding: `${backrs.spacing['spacing-xxs']} ${backrs.spacing['spacing-sm']}`,
  maxWidth: '100%',
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

const salaryTextSx: SxProps<Theme> = ({ typography, backrs }) => ({
  ...typography['text-sm-regular'],
  color: backrs.colors['Colors/Text/text-tertiary'],
  whiteSpace: 'nowrap',
});

// ── Mobile styles ──

const mobileContainerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  borderRadius: backrs.radius['radius-lg'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary-hover']}`,
  overflow: 'hidden',
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const mobileHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: `${backrs.spacing['spacing-lg']} ${backrs.spacing['spacing-2xl']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const trackSelectSx: SxProps<Theme> = ({ backrs }) => ({
  flex: 1,
  marginRight: backrs.spacing['spacing-2xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  borderRadius: backrs.radius['radius-md'],
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: backrs.colors['Colors/Border/border-primary'],
  },
  '& .MuiSelect-select': {
    padding: `${backrs.spacing['spacing-sm']} ${backrs.spacing['spacing-lg']}`,
  },
});

const selectIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingRight: backrs.spacing['spacing-md'],
  pointerEvents: 'none',
});

const trackSelectLabelSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const mobileDataContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: backrs.spacing['spacing-2xl'],
  gap: backrs.spacing['spacing-2xl'],
});

const mobileRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const mobileLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
});

const mobileValueSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const dataSourcesIconSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: backrs.spacing['spacing-lg'],
  right: backrs.spacing['spacing-lg'],
  cursor: 'pointer',
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
});
